import { Spinner } from "@jammable/ui-components/Loader";
import { Repeat, X } from "lucide-react";
import Image from "next/image";
import React, { useState, useRef, useContext, createContext } from "react";
import { useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";

const AudioPlayerContext = createContext();

export const useAudioPlayer = () => {
  return useContext(AudioPlayerContext);
};

const GlobalAudioPlayer = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [info, setInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);

  const playAudio = ({ audio, info }) => {
    if (audioSrc !== audio) {
      setAudioSrc(audio);
      setInfo(info);
      setCurrentTime(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    setIsPlaying(false);
    audioRef.current?.pause();
  };

  // Sync audio element with source changes
  useEffect(() => {
    if (!audioRef.current || !audioSrc) return;
    audioRef.current.src = audioSrc;
    audioRef.current.load();
  }, [audioSrc]);

  // Sync play/pause state with audio element
  useEffect(() => {
    if (!audioRef.current || !audioSrc) return;
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, audioSrc]);

  const handleScrub = (event) => {
    const scrubTime =
      (event.nativeEvent.offsetX / event.target.clientWidth) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = scrubTime;
    }
    setCurrentTime(scrubTime);
  };

  const [repeat, setRepeat] = useState(false);

  const [isVisible, setIsVisible] = useState(false);
  const [isHidden, setIsHidden] = useState(true);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (audioSrc) {
      setIsHidden(false);
      setTimeout(() => {
        setIsVisible(true);
      }, 50);
    } else {
      setIsVisible(false);
      setTimeout(() => {
        setIsHidden(true);
      }, 300);
    }
  }, [audioSrc]);

  return (
    <AudioPlayerContext.Provider
      value={{ playAudio, pauseAudio, isPlaying, info }}
    >
      {children}
      <>
        {isMounted && (
          <div
            className={`tw-w-full tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-justify-center tw-flex tw-pb-2 tw-px-2 ${isHidden ? "tw-hidden" : ""}`}
          >
            <div
              className={`tw-bg-white tw-z-50 tw-overflow-visible tw-flex tw-relative tw-justify-center tw-max-w-4xl tw-w-full tw-shadow-2xl tw-rounded-3xl tw-p-3 tw-transition-all ${
                isVisible
                  ? "tw-opacity-100 tw-h-[84px] tw-scale-100"
                  : "tw-opacity-0 tw-h-0  tw-p-0"
              }`}
            >
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pauseAudio();
                  setAudioSrc(null);
                  setInfo(null);
                }}
                className="tw-absolute tw-size-6 tw-flex tw-items-center tw-justify-center tw--top-1 tw--right-1 tw-text-white tw-p-1 tw-bg-purple-700/90 tw-rounded-full tw-cursor-pointer"
              >
                <X size={14} strokeWidth={4} />
              </div>
              <audio
                ref={audioRef}
                onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                onEnded={() => {
                  if (repeat) {
                    audioRef.current.currentTime = 0;
                    audioRef.current.play().catch(() => {});
                  } else {
                    setIsPlaying(false);
                  }
                }}
                onCanPlay={() => setIsLoading(false)}
                onWaiting={() => setIsLoading(true)}
                onPlaying={() => setIsLoading(false)}
                style={{ display: "none" }}
              />
              {audioSrc && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-truncate">
                  <div className="tw-relative tw-rounded-full tw-bg-purple-700 tw-flex tw-items-center tw-justify-center">
                    <Image
                      className="tw-rounded-2xl"
                      src={info?.image}
                      alt="audio"
                      width={60}
                      height={60}
                    />
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isPlaying) {
                          pauseAudio();
                        } else {
                          setIsPlaying(true);
                        }
                      }}
                      className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-bg-black/40 hover:tw-bg-black/50 tw-transition-all  tw-text-white tw-rounded-2xl "
                    >
                      {isLoading ? (
                        <Spinner
                          className="tw-border-white tw-border-3"
                          size={6}
                        />
                      ) : isPlaying ? (
                        <FaPause />
                      ) : (
                        <FaPlay />
                      )}
                    </div>
                  </div>
                  <div className="tw-flex tw-flex-col tw-w-full tw-truncate">
                    <div className="tw-text-base tw-font-black tw-truncate">
                      {info.name}
                    </div>
                    <div className="tw-text-sm tw-truncate tw-font-bold  tw-text-black/40 tw-mb-1.5">
                      {info.artistNames}
                    </div>
                    <div
                      className="tw-w-full tw-z-10 tw-h-1.5 tw-bg-gray-200 tw-rounded-full tw-relative tw-cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); handleScrub(e); }}
                    >
                      <div
                        className="tw-h-full tw-bg-purple-700 tw-rounded-full"
                        style={{
                          width: `${(currentTime / duration) * 100 || 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <div
                      className={`tw-flex tw-size-10  tw-w-10 tw-min-w-10 tw-items-center tw-justify-center tw-transition-all  tw-rounded-2xl tw-text-purple-700 tw-cursor-pointer ${
                        repeat
                          ? "tw-bg-purple-700/20  hover:tw-bg-purple-700/30"
                          : "tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-600"
                      }`}
                      onClick={(e) => { e.stopPropagation(); setRepeat(!repeat); }}
                    >
                      <Repeat size={20} strokeWidth={3} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    </AudioPlayerContext.Provider>
  );
};

export default GlobalAudioPlayer;
